import { Link } from "@/lib/nav";
import { Fragment } from "react";

import FilterChips from "@/components/filter-chips";
import Money from "@/components/money";
import SectionCard from "@buildpy/ui/section-card";
import { cn } from "@/lib/utils";
import {
  CUSTOMER_STATEMENT_NOTE,
  customerStatementPath,
  STATEMENT_GROUPING_LABEL,
  STATEMENT_GROUPINGS,
  type CurrentStatement,
  type StatementGrouping,
} from "@buildpy/core/modules/change-order/current-statement";
import CurrentStatementCards from "@/modules/change-order/ui/current-statement-cards";
import CurrentStatementTable from "@/modules/change-order/ui/current-statement-table";
import { deltaToneClass } from "@buildpy/core/modules/change-order/view";
import CurrentStatementDocument from "@/modules/print/current-statement-document";

interface Props {
  token: string;
  statement: CurrentStatement;
  // 고객이 지금 승인할 수 있는 변경공사 건수(재발송 대기 제외) — 계약서 화면의 변경공사 칸으로 보낸다.
  awaiting: number;
  contractHref: string;
  contractNo: string;
  contractTitle: string;
  asOf: string;
  // 묶음 칩 링크 — 기본은 고객 링크 경로, 데모처럼 다른 경로에서 그릴 때만 바꾼다.
  groupingHref?: (grouping: StatementGrouping) => string;
}

// 고객 현재 내역서 — 폰 먼저: 요약은 세로로 쌓고, 줄은 폰에서 항목 카드·md 이상에서 표로 보인다(인쇄는 종이 문서 한 장).
const CustomerStatementView = ({ token, statement, awaiting, contractHref, contractNo, contractTitle, asOf, groupingHref }: Props) => {
  const t = statement.totals;
  const hrefOf = groupingHref ?? ((g: StatementGrouping) => customerStatementPath(token, g));
  return (
    <>
      <div className="print-hide mx-auto flex w-full max-w-3xl flex-col gap-y-4 px-4 pt-4" data-testid="customer-statement">
        <SectionCard className="text-sm" data-testid="customer-statement-summary">
          <h1 className="text-base font-semibold">현재 내역서</h1>
          <p className="break-words text-xs text-muted-foreground">{contractTitle}</p>
          <dl className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
            <dt className="text-muted-foreground">원 계약금액</dt>
            <dd className="whitespace-nowrap text-right tabular-nums"><Money value={t.total.original} /></dd>
            {statement.columns.map((c) => (
              <Fragment key={c.id}>
                <dt className="break-words text-muted-foreground">
                  {c.seq}차 변경 · {c.title}
                </dt>
                <dd className={cn("whitespace-nowrap text-right tabular-nums", deltaToneClass(c.delta_total))}><Money signed value={c.delta_total} /></dd>
              </Fragment>
            ))}
            <dt className="font-semibold">현재 계약금액</dt>
            <dd className="whitespace-nowrap text-right font-semibold tabular-nums" data-testid="customer-statement-total">
              <Money value={t.total.current} />
            </dd>
            <dt className="text-muted-foreground">공급가 · 부가세</dt>
            <dd className="whitespace-nowrap text-right text-xs tabular-nums text-muted-foreground">
              <Money value={t.current_supply} /> · <Money value={t.current_vat} />
            </dd>
          </dl>
          <p className="mt-3 text-xs text-muted-foreground" data-testid="customer-statement-note">
            {CUSTOMER_STATEMENT_NOTE}
            {statement.columns.length === 0 && " 아직 승인된 변경공사가 없어 계약 내역만 보입니다."}
          </p>
          {awaiting > 0 && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="customer-statement-awaiting">
              확인을 기다리는 변경공사 {awaiting}건은 승인하시면 반영됩니다.{" "}
              <Link className="font-medium text-primary underline-offset-2 hover:underline" href={`${contractHref}#change-orders`}>
                확인하기
              </Link>
            </p>
          )}
        </SectionCard>

        <FilterChips
          activeHref={hrefOf(statement.grouping)}
          aria-label="묶음"
          items={STATEMENT_GROUPINGS.map((g) => ({ label: STATEMENT_GROUPING_LABEL[g], href: hrefOf(g) }))}
        />

        <SectionCard className="hidden min-w-0 md:block" padding="sm" data-testid="customer-statement-lines">
          <CurrentStatementTable audience="customer" statement={statement} />
        </SectionCard>
        <CurrentStatementCards audience="customer" statement={statement} />
      </div>

      <div className="print-root hidden print:block">
        <CurrentStatementDocument asOf={asOf} audience="customer" contractNo={contractNo} contractTitle={contractTitle} statement={statement} />
      </div>
    </>
  );
};

export default CustomerStatementView;
